import api from './api';

export interface Ad {
  id: string;
  name: string;
  destinationUrl: string;
  trackingUrl: string;
  trackingCode: string;
  userTrackingEnabled: boolean;
  active: boolean;
  description?: string;
  createdBy: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  clickCount?: number;
  uniqueUsersCount?: number;
}

export interface CreateAdRequest {
  name: string;
  destinationUrl: string;
  description?: string;
}

export interface UpdateAdRequest {
  name?: string;
  destinationUrl?: string;
  description?: string;
  userTrackingEnabled?: boolean;
  active?: boolean;
}

export interface AnalyticsQuery {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface AdAnalytics {
  totalClicks: number;
  uniqueUsers: number;
  clicksByDate: { date: string; clicks: number }[];
  clicksByCountry: { country: string; clicks: number }[];
  clicksByDevice: { device: string; clicks: number }[];
  clicksByBrowser: { browser: string; clicks: number }[];
  topReferrers: { referrer: string; clicks: number }[];
  recentEvents: {
    id: string;
    createdAt: string;
    ipAddress: string;
    userAgent?: string;
    referrer?: string;
    countryCode?: string;
    city?: string;
    deviceType?: string;
    browser?: string;
    userId?: string;
  }[];
}

export interface TrackingAnalytics {
  adId: string;
  adName: string;
  totalClicks: number;
  uniqueUsers: number;
  clicksByDate: { date: string; clicks: number }[];
  clicksByCountry: { country: string; clicks: number }[];
  clicksByDevice: { device: string; clicks: number }[];
  clicksByBrowser: { browser: string; clicks: number }[];
  topReferrers: { referrer: string; clicks: number }[];
  recentEvents: {
    id: string;
    createdAt: string;
    ipAddress: string;
    userAgent?: string;
    referrer?: string;
    countryCode?: string;
    city?: string;
    deviceType?: string;
    browser?: string;
    userId?: string;
  }[];
}

export interface AnalyticsSummary {
  summary: {
    totalAds: number;
    totalClicks: number;
    totalUniqueUsers: number;
    averageClicksPerAd: number;
  };
  ads: {
    adId: string;
    adName: string;
    totalClicks: number;
    uniqueUsers: number;
  }[];
}

export interface TopPerformers {
  metric: string;
  limit: number;
  topPerformers: {
    adId: string;
    adName: string;
    destinationUrl: string;
    totalClicks: number;
    uniqueUsers: number;
  }[];
}

export interface ClicksByUser {
  adCount: number;
  data: Array<{
    adId: string;
    adName: string;
    users: Array<{
      userId?: number;
      firstName?: string;
      lastName?: string;
      email?: string;
      displayName: string;
      clickCount: number;
    }>;
    totalClicks: number;
  }>;
}

export class AdsService {
  private static readonly BASE_PATH = '/ads';

  // CRUD Operations
  static async getAds(dateFilters?: {
    startDate?: string;
    endDate?: string;
  }): Promise<Ad[]> {
    const params = new URLSearchParams();
    if (dateFilters?.startDate)
      params.append('startDate', dateFilters.startDate);
    if (dateFilters?.endDate) params.append('endDate', dateFilters.endDate);

    const url = `${this.BASE_PATH}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  static async getAd(id: string): Promise<Ad> {
    const response = await api.get(`${this.BASE_PATH}/${id}`);
    return response.data;
  }

  static async createAd(data: CreateAdRequest): Promise<Ad> {
    const response = await api.post(this.BASE_PATH, data);
    return response.data;
  }

  static async updateAd(id: string, data: UpdateAdRequest): Promise<Ad> {
    const response = await api.patch(`${this.BASE_PATH}/${id}`, data);
    return response.data;
  }

  static async deleteAd(id: string): Promise<void> {
    await api.delete(`${this.BASE_PATH}/${id}`);
  }

  static async toggleTracking(id: string): Promise<Ad> {
    const response = await api.patch(`${this.BASE_PATH}/${id}/toggle-tracking`);
    return response.data;
  }

  // Analytics Operations (Legacy - using database)
  static async getAnalytics(
    id: string,
    query?: AnalyticsQuery
  ): Promise<AdAnalytics> {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.offset) params.append('offset', query.offset.toString());

    const url = `${this.BASE_PATH}/${id}/analytics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  // Tracking Service Analytics (Enhanced)
  static async getTrackingAnalytics(
    id: string,
    query?: AnalyticsQuery
  ): Promise<TrackingAnalytics> {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.offset) params.append('offset', query.offset.toString());

    const url = `${this.BASE_PATH}/${id}/tracking-analytics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  static async getAnalyticsSummary(data: {
    adIds: string[];
    startDate?: string;
    endDate?: string;
  }): Promise<AnalyticsSummary> {
    const response = await api.post(
      `${this.BASE_PATH}/analytics-summary`,
      data
    );
    return response.data;
  }

  static async getTopPerformers(query?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
    metric?: 'clicks' | 'unique_users';
  }): Promise<TopPerformers> {
    const params = new URLSearchParams();
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    if (query?.metric) params.append('metric', query.metric);

    const url = `${this.BASE_PATH}/top-performers${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  static async getClicksByUser(data: {
    adIds: string[];
    startDate?: string;
    endDate?: string;
  }): Promise<ClicksByUser> {
    const response = await api.post(
      `${this.BASE_PATH}/analytics/clicks-by-user`,
      data
    );
    return response.data;
  }

  static async checkTrackingServiceHealth(): Promise<{
    status: string;
    timestamp: string;
  }> {
    const response = await api.get(`${this.BASE_PATH}/tracking-service/health`);
    return response.data;
  }

  // Utility Functions
  static generateTrackingUrl(
    ad: Ad,
    userId?: string,
    utmParams?: Record<string, string>
  ): string {
    const url = new URL(ad.trackingUrl);

    if (userId) {
      url.searchParams.set('user_id', userId);
    }

    if (utmParams) {
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });
    }

    return url.toString();
  }

  static async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    }
  }

  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }
}
